import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Plus, Trash2, Loader2, Save, X, ChevronDown, Eye, Tag, AlertCircle, ChevronRight, Copy } from 'lucide-react';
import { toast } from 'sonner';
import * as api from '../api';
import ModeleEmails from './settings/ModeleEmails';
import { useSociete } from '../context/SocieteContext';

// ─── Styles ───────────────────────────────────────────────────────────────────
const inp = {
  border: '1px solid var(--border, #E2E8F0)', borderRadius: 6,
  padding: '6px 10px', fontSize: 13, background: '#fff',
  width: '100%', boxSizing: 'border-box',
};
const btnBlue = {
  display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px',
  background: '#2563EB', color: '#fff', border: 'none', borderRadius: 6,
  fontSize: 13, cursor: 'pointer', fontWeight: 500,
};
const btnGhost = {
  display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px',
  background: 'transparent', color: 'var(--text-muted)', border: '1px solid var(--border)',
  borderRadius: 6, fontSize: 13, cursor: 'pointer',
};
const L = { fontSize: 12, fontWeight: 500, color: 'var(--text-muted)' };

// ─── Variables placeholder ────────────────────────────────────────────────────
const CAMPAGNE_VARS = [
  { cle: 'prenom',     label: 'Prénom du contact' },
  { cle: 'nom',        label: 'Nom du contact' },
  { cle: 'prenom_nom', label: 'Prénom + Nom' },
  { cle: 'entreprise', label: 'Entreprise liée' },
  { cle: 'periode',    label: 'Période (rapport)' },
];

const FAKE_VALS = {
  prenom: 'Sophie', nom: 'Leclerc', prenom_nom: 'Sophie Leclerc',
  entreprise: 'Dupont Construction', periode: 'Mai – Décembre 2026',
};

function applyFake(text) {
  if (!text) return '';
  return Object.entries(FAKE_VALS).reduce(
    (t, [k, v]) => t.replace(new RegExp('\\{' + k + '\\}', 'g'), v),
    text,
  );
}

function buildPreviewHtml(sujet, snippet, corps, logoUrl) {
  const body = applyFake(corps || '').replace(/\n/g, '<br/>');
  const preheader = snippet
    ? `<span style="display:none;font-size:1px;max-height:0;opacity:0;overflow:hidden;">${applyFake(snippet)}</span>`
    : '';
  const isLogo = !!logoUrl;
  const hdrBg = isLogo ? 'background:white;' : 'background:linear-gradient(135deg,#1A3A6E 0%,#2563EB 100%);';
  const logoTag = isLogo ? `<img src="${logoUrl}" alt="Logo" style="display:block;max-height:48px;width:auto;border:0;" />` : '';
  const brand = isLogo ? '' : '<span style="color:white;font-size:20px;font-weight:700;">Bedee</span>';
  return `<!DOCTYPE html><html lang="fr">
<head><meta charset="UTF-8"></head>
<body style="margin:0;padding:0;background:#F1F5F9;font-family:'Helvetica Neue',Arial,sans-serif;">
${preheader}
<table width="100%" cellpadding="0" cellspacing="0" style="background:#F1F5F9;padding:40px 20px;">
  <tr><td align="center">
    <table width="600" cellpadding="0" cellspacing="0" style="max-width:600px;width:100%;">
      <tr><td style="${hdrBg}padding:24px 32px;border-radius:12px 12px 0 0;text-align:left;">
        <table cellpadding="0" cellspacing="0"><tr>
          <td style="padding-right:12px;vertical-align:middle;">${logoTag}</td>
          <td style="vertical-align:middle;">${brand}</td>
        </tr></table>
      </td></tr>
      <tr><td style="background:white;padding:36px 32px;border-radius:0 0 12px 12px;">
        <h1 style="margin:0 0 16px;color:#0F172A;font-size:22px;font-weight:700;">${applyFake(sujet || '')}</h1>
        <div style="color:#334155;font-size:15px;line-height:1.8;">${body}</div>
      </td></tr>
      <tr><td style="padding:20px 32px;text-align:center;">
        <p style="margin:0;color:#94A3B8;font-size:12px;">Bedee · Gestion de parc matériel<br>Cet email a été envoyé automatiquement.</p>
      </td></tr>
    </table>
  </td></tr>
</table>
</body></html>`;
}

// ─── Modale prévisualisation ──────────────────────────────────────────────────
function PreviewModal({ sujet, snippet, corps, logoUrl, onClose }) {
  const html = buildPreviewHtml(sujet, snippet, corps, logoUrl);
  const iframeRef = useRef(null);
  const handleLoad = useCallback(() => {
    const f = iframeRef.current;
    if (f?.contentDocument?.documentElement) {
      f.style.height = (f.contentDocument.documentElement.scrollHeight + 8) + 'px';
    }
  }, []);
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(15,23,42,0.6)',
      backdropFilter: 'blur(4px)', overflowY: 'auto', padding: '32px 16px' }}
      onClick={onClose}>
      <div style={{ maxWidth: 620, margin: '0 auto', background: '#fff', borderRadius: 16,
        boxShadow: '0 24px 48px rgba(0,0,0,0.18)' }}
        onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '12px 20px', borderBottom: '1px solid #E2E8F0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Eye size={14} style={{ color: '#2563EB' }} />
            <span style={{ fontSize: 13, fontWeight: 600, color: '#0F172A' }}>Prévisualisation email</span>
          </div>
          <button onClick={onClose} data-testid="preview-close-btn"
            style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', padding: 4 }}>
            <X size={14} style={{ color: '#64748B' }} />
          </button>
        </div>
        <div style={{ padding: '10px 20px', borderBottom: '1px solid #E2E8F0', background: '#F8FAFC',
          display: 'flex', gap: 8, alignItems: 'center' }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: '#64748B' }}>Sujet :</span>
          <span style={{ fontSize: 13, color: '#0F172A' }}>{applyFake(sujet || '')}</span>
        </div>
        {snippet && (
          <div style={{ padding: '8px 20px', borderBottom: '1px solid #E2E8F0', background: '#F8FAFC',
            display: 'flex', gap: 8, alignItems: 'center' }}>
            <span style={{ fontSize: 11, fontWeight: 600, color: '#64748B' }}>Snippet :</span>
            <span style={{ fontSize: 12, color: '#475569', fontStyle: 'italic' }}>{applyFake(snippet)}</span>
          </div>
        )}
        <div style={{ padding: '8px 20px', borderBottom: '1px solid #E2E8F0', background: '#FFFBEB',
          display: 'flex', alignItems: 'center', gap: 6 }}>
          <AlertCircle size={11} style={{ color: '#B45309' }} />
          <span style={{ fontSize: 11, color: '#92400E' }}>Aperçu avec données fictives</span>
        </div>
        <iframe ref={iframeRef} title="Aperçu email" srcDoc={html} onLoad={handleLoad}
          data-testid="preview-iframe"
          style={{ width: '100%', height: 500, border: 'none', display: 'block',
            borderRadius: '0 0 16px 16px' }}
          sandbox="allow-same-origin" scrolling="no" />
      </div>
    </div>
  );
}

// ─── Sélecteur PDF ────────────────────────────────────────────────────────────
function PdfSelector({ selected, onChange, generators }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  const toggle = (id) => onChange(selected.includes(id) ? selected.filter(x => x !== id) : [...selected, id]);
  const grouped = generators.reduce((acc, g) => { (acc[g.categorie] = acc[g.categorie] || []).push(g); return acc; }, {});
  const selectedItems = generators.filter(g => selected.includes(g.pdf_generator_id));
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div ref={ref} style={{ position: 'relative' }}>
        <button type="button" onClick={() => setOpen(o => !o)} data-testid="pdf-selector-toggle"
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            width: '100%', ...inp, cursor: 'pointer' }}>
          <span style={{ color: selected.length === 0 ? '#9CA3AF' : 'inherit' }}>
            {selected.length === 0 ? '— Aucun PDF sélectionné —' : `${selected.length} PDF sélectionné${selected.length > 1 ? 's' : ''}`}
          </span>
          <ChevronDown size={14} style={{ color: '#9CA3AF', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />
        </button>
        {open && (
          <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50, marginTop: 4,
            background: '#fff', border: '1px solid var(--border)', borderRadius: 8,
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)', maxHeight: 300, overflowY: 'auto' }}>
            {Object.entries(grouped).map(([cat, items]) => (
              <div key={cat}>
                <div style={{ padding: '7px 12px 4px', fontSize: 10, fontWeight: 700,
                  textTransform: 'uppercase', letterSpacing: '0.07em', color: '#64748B',
                  borderBottom: '1px solid #F1F5F9', background: '#F8FAFC' }}>{cat}</div>
                {items.map(g => (
                  <label key={g.pdf_generator_id} data-testid={`pdf-option-${g.service_handler}`}
                    style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 14px',
                      cursor: 'pointer', fontSize: 13,
                      background: selected.includes(g.pdf_generator_id) ? '#EFF6FF' : '#fff' }}>
                    <input type="checkbox" checked={selected.includes(g.pdf_generator_id)}
                      onChange={() => toggle(g.pdf_generator_id)} style={{ accentColor: '#2563EB' }} />
                    <span style={{ flex: 1 }}>{g.nom}</span>
                    <span style={{ fontSize: 10, fontFamily: 'monospace', color: '#64748B',
                      background: '#F1F5F9', padding: '1px 5px', borderRadius: 3 }}>{g.resolver_type}</span>
                  </label>
                ))}
              </div>
            ))}
            {generators.length === 0 && (
              <div style={{ padding: 14, fontSize: 13, color: '#9CA3AF', textAlign: 'center' }}>
                Aucun générateur PDF disponible
              </div>
            )}
          </div>
        )}
      </div>
      {selectedItems.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {selectedItems.map(g => (
            <span key={g.pdf_generator_id} style={{ display: 'inline-flex', alignItems: 'center', gap: 5,
              padding: '3px 8px 3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 500,
              background: '#EFF6FF', color: '#1D4ED8', border: '1px solid #BFDBFE' }}>
              {g.nom}
              <button type="button" onClick={() => toggle(g.pdf_generator_id)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                  display: 'flex', alignItems: 'center', color: '#3B82F6' }}>
                <X size={11} />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Formulaire modèle campagne ────────────────────────────────────────────────
function CampagneTemplateForm({ initial, onSave, onCancel, onDuplicate, onDelete, logoUrl }) {
  const [form, setForm] = useState({
    nom:        initial?.nom        || '',
    sujet:      initial?.sujet      || '',
    snippet:    initial?.snippet    || '',
    corps_html: initial?.corps_html || '',
  });
  const [selectedPdfs, setSelectedPdfs] = useState([]);
  const [generators,   setGenerators]   = useState([]);
  const [saving,       setSaving]       = useState(false);
  const [showPreview,  setShowPreview]  = useState(false);

  const sujetRef   = useRef(null);
  const snippetRef = useRef(null);
  const corpsRef   = useRef(null);
  const lastField  = useRef('corps_html');

  const FIELD_REFS = { sujet: sujetRef, snippet: snippetRef, corps_html: corpsRef };
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  useEffect(() => {
    api.getPdfGenerators().then(r => setGenerators(r.data || [])).catch(() => {});
    if (initial?.template_id) {
      api.getTemplatePdfAttachments(initial.template_id)
        .then(r => setSelectedPdfs((r.data || []).map(a => a.pdf_generator_id)))
        .catch(() => {});
    }
  }, [initial?.template_id]);

  const insertVar = (cle) => {
    const fieldName = lastField.current;
    const ref = FIELD_REFS[fieldName];
    const el = ref?.current;
    const tag = `{${cle}}`;
    if (!el) { set(fieldName, (form[fieldName] || '') + tag); return; }
    const start = el.selectionStart ?? el.value.length;
    const end   = el.selectionEnd   ?? el.value.length;
    const newVal = el.value.slice(0, start) + tag + el.value.slice(end);
    set(fieldName, newVal);
    setTimeout(() => {
      el.focus();
      const pos = start + tag.length;
      el.setSelectionRange(pos, pos);
    }, 0);
  };

  const handleSubmit = async () => {
    if (!form.nom.trim())   { toast.error('Nom obligatoire');   return; }
    if (!form.sujet.trim()) { toast.error('Sujet obligatoire'); return; }
    setSaving(true);
    try { await onSave({ ...form, pdf_generator_ids: selectedPdfs }); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, overflowY: 'auto', flex: 1, padding: '20px 24px' }}>

      {/* Nom */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span style={L}>Nom du modèle *</span>
        <input value={form.nom} onChange={e => set('nom', e.target.value)}
          placeholder="Ex: Newsletter mensuelle" style={inp} data-testid="campagne-tpl-nom" />
      </div>

      {/* Sujet */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span style={L}>Sujet de l'email *</span>
        <input ref={sujetRef} value={form.sujet}
          onFocus={() => { lastField.current = 'sujet'; }}
          onChange={e => set('sujet', e.target.value)}
          placeholder="Ex: Rapport {entreprise} — {periode}"
          style={inp} data-testid="campagne-tpl-sujet" />
      </div>

      {/* Snippet */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span style={L}>Snippet / Preheader</span>
        <input ref={snippetRef} value={form.snippet}
          onFocus={() => { lastField.current = 'snippet'; }}
          onChange={e => set('snippet', e.target.value)}
          placeholder="Texte court affiché avant ouverture de l'email…"
          style={inp} data-testid="campagne-tpl-snippet" />
      </div>

      {/* Pièces jointes PDF */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span style={L}>Pièces jointes PDF</span>
        <PdfSelector selected={selectedPdfs} onChange={setSelectedPdfs} generators={generators} />
      </div>

      {/* Variables */}
      <div style={{ padding: '10px 12px', background: '#F8FAFC', border: '1px solid #E2E8F0',
        borderRadius: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Tag size={12} style={{ color: '#2563EB' }} />
          <span style={{ fontSize: 11, fontWeight: 600, color: '#475569' }}>
            Variables — cliquez pour insérer dans le champ actif
          </span>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {CAMPAGNE_VARS.map(v => (
            <button key={v.cle} type="button" onClick={() => insertVar(v.cle)}
              data-testid={`var-chip-${v.cle}`} title={v.label}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 4,
                padding: '2px 8px', border: '1px solid #BFDBFE', borderRadius: 4,
                background: '#EFF6FF', color: '#1D4ED8', fontSize: 12,
                fontFamily: 'monospace', cursor: 'pointer' }}>
              {'{' + v.cle + '}'}
            </button>
          ))}
        </div>
      </div>

      {/* Corps HTML */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span style={L}>Corps de l'email</span>
        <textarea ref={corpsRef} value={form.corps_html}
          onFocus={() => { lastField.current = 'corps_html'; }}
          onChange={e => set('corps_html', e.target.value)}
          rows={10}
          placeholder={'<p>Bonjour {prenom},</p>\n<p>Veuillez trouver ci-joint...</p>'}
          style={{ ...inp, resize: 'vertical', fontFamily: 'monospace', fontSize: 12 }}
          data-testid="campagne-tpl-corps" />
        <span style={{ fontSize: 10, color: '#94A3B8' }}>
          HTML ou texte brut. Les sauts de ligne sont convertis automatiquement en &lt;br/&gt;.
        </span>
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap',
        paddingTop: 8, borderTop: '1px solid var(--border)', marginTop: 4 }}>
        {/* Gauche — actions secondaires */}
        <div style={{ flex: 1, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {initial && onDuplicate && (
            <button onClick={onDuplicate}
              data-testid={`tpl-duplicate-${initial.template_id}`}
              style={{ fontSize: 12, padding: '5px 10px', borderRadius: 6, border: '1px solid var(--border)',
                background: '#fff', color: '#64748B', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
              <Copy size={12} /> Dupliquer
            </button>
          )}
          {initial && onDelete && (
            <button onClick={onDelete}
              data-testid={`tpl-delete-${initial.template_id}`}
              style={{ fontSize: 12, padding: '5px 10px', borderRadius: 6, border: '1px solid var(--border)',
                background: '#fff', color: '#9CA3AF', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
              <Trash2 size={12} /> Supprimer
            </button>
          )}
          <button onClick={onCancel} style={btnGhost} data-testid="campagne-tpl-cancel">
            <X size={13} /> Annuler
          </button>
          <button onClick={() => setShowPreview(true)} style={btnGhost} data-testid="campagne-tpl-preview">
            <Eye size={13} /> Prévisualiser
          </button>
        </div>
        {/* Droite — enregistrer */}
        <button onClick={handleSubmit} disabled={saving} style={{ ...btnBlue }}
          data-testid="campagne-tpl-save">
          {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
          Enregistrer
        </button>
      </div>

      {showPreview && (
        <PreviewModal
          sujet={form.sujet}
          snippet={form.snippet}
          corps={form.corps_html}
          logoUrl={logoUrl}
          onClose={() => setShowPreview(false)}
        />
      )}
    </div>
  );
}

// ─── Onglet Campagne ───────────────────────────────────────────────────────────
function OngletCampagne() {
  const [templates, setTemplates] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [sidePanel, setSidePanel] = useState(null); // null | { mode: 'new'|'edit', data: null|obj }
  const [pdfNames,  setPdfNames]  = useState({});   // { template_id: string[] }
  const { activeAgences }         = useSociete();

  const [logoUrl, setLogoUrl] = useState(null);
  useEffect(() => {
    const id = activeAgences[0]?.agence_id;
    if (!id) { setLogoUrl(null); return; }
    const url = `${process.env.REACT_APP_BACKEND_URL || ''}/api/public/agence-logo/${id}`;
    const img = new window.Image();
    img.onload  = () => setLogoUrl(url);
    img.onerror = () => setLogoUrl(null);
    img.src = url;
  }, [activeAgences]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.getCampagneTemplates();
      const tpls = r.data || [];
      setTemplates(tpls);
      // Chargement parallèle des pièces jointes PDF pour le tableau
      const map = {};
      await Promise.all(tpls.map(async (t) => {
        try {
          const pr = await api.getTemplatePdfAttachments(t.template_id);
          map[t.template_id] = (pr.data || []).map(a => a.nom || a.service_handler);
        } catch { map[t.template_id] = []; }
      }));
      setPdfNames(map);
    } catch { toast.error('Erreur chargement des modèles'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (data) => {
    const { pdf_generator_ids, ...templateData } = data;
    try {
      let tid;
      if (sidePanel.mode === 'new') {
        const r = await api.createCampagneTemplate(templateData);
        tid = r.data?.template_id;
        toast.success('Modèle créé');
      } else {
        const r = await api.updateCampagneTemplate(sidePanel.data.template_id, templateData);
        tid = r.data?.template_id || sidePanel.data.template_id;
        toast.success('Modèle mis à jour');
      }
      if (tid && pdf_generator_ids !== undefined) {
        await api.setTemplatePdfAttachments(tid, pdf_generator_ids);
      }
      setSidePanel(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };

  const handleDuplicate = async (tpl) => {
    try {
      await api.duplicateCampagneTemplate(tpl.template_id);
      toast.success(`Modèle "${tpl.nom}" dupliqué`);
      load();
    } catch (e2) { toast.error(e2?.response?.data?.detail || 'Erreur duplication'); }
  };

  const handleDelete = async (tpl) => {
    if (!window.confirm(`Supprimer le modèle "${tpl.nom}" ?`)) return;
    try {
      await api.deleteCampagneTemplate(tpl.template_id);
      toast.success('Modèle supprimé');
      if (sidePanel?.data?.template_id === tpl.template_id) setSidePanel(null);
      load();
    } catch (e2) { toast.error(e2?.response?.data?.detail || 'Erreur'); }
  };

  const thStyle = { padding: '9px 14px', fontSize: 11, fontWeight: 600, textTransform: 'uppercase',
    letterSpacing: '0.05em', color: '#6B7280', background: '#F9FAFB', textAlign: 'left' };
  const tdStyle = { padding: '10px 14px', fontSize: 13, textAlign: 'left', color: '#374151' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <p style={{ margin: 0, fontSize: 13, color: 'var(--text-muted)' }}>
          {templates.length} modèle{templates.length !== 1 ? 's' : ''} de campagne
        </p>
        <button onClick={() => setSidePanel({ mode: 'new', data: null })} style={btnBlue}
          data-testid="campagne-tpl-new">
          <Plus size={14} /> Nouveau modèle
        </button>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Loader2 size={22} className="animate-spin" style={{ color: '#9CA3AF' }} />
        </div>
      ) : templates.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, fontSize: 14, color: 'var(--text-muted)' }}>
          Aucun modèle de campagne. Créez-en un ci-dessus.
        </div>
      ) : (
        <div style={{ border: '1px solid #E5E7EB', borderRadius: 8, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #E5E7EB' }}>
                <th style={thStyle}>Nom du modèle</th>
                <th style={thStyle}>Sujet</th>
                <th style={thStyle}>Pièces jointes PDF</th>
              </tr>
            </thead>
            <tbody>
              {templates.map((tpl, idx) => {
                const isActive = sidePanel?.data?.template_id === tpl.template_id;
                const pdfs     = pdfNames[tpl.template_id];
                return (
                  <tr key={tpl.template_id}
                    data-testid={`tpl-row-${tpl.template_id}`}
                    onClick={() => setSidePanel(isActive ? null : { mode: 'edit', data: tpl })}
                    style={{
                      borderBottom: idx < templates.length - 1 ? '1px solid #E5E7EB' : 'none',
                      cursor: 'pointer',
                      background: isActive ? '#EFF6FF' : '#fff',
                      transition: 'background 0.1s',
                    }}
                    onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = '#F9FAFB'; }}
                    onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = '#fff'; }}
                  >
                    <td style={{ ...tdStyle, fontWeight: 500 }}>{tpl.nom}</td>
                    <td style={{ ...tdStyle, color: '#6B7280', maxWidth: 280,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {tpl.sujet || '—'}
                    </td>
                    <td style={{ ...tdStyle }}>
                      {pdfs === undefined
                        ? <Loader2 size={12} className="animate-spin" style={{ color: '#9CA3AF' }} />
                        : pdfs.length === 0
                          ? <span style={{ color: '#9CA3AF' }}>—</span>
                          : <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                              {pdfs.map(n => (
                                <span key={n} style={{ display: 'inline-flex', alignItems: 'center',
                                  padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 500,
                                  background: '#EFF6FF', color: '#1D4ED8', border: '1px solid #BFDBFE' }}>
                                  {n}
                                </span>
                              ))}
                            </div>
                      }
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Overlay + Side panel */}
      {sidePanel && (
        <>
          <div onClick={() => setSidePanel(null)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.18)', zIndex: 999 }} />
          <div style={{ position: 'fixed', top: 0, right: 0, width: 816, height: '100vh',
            background: '#fff', zIndex: 1000, boxShadow: '-6px 0 32px rgba(0,0,0,0.12)',
            display: 'flex', flexDirection: 'column', animation: 'slideInRight 0.2s ease' }}>
            {/* Header */}
            <div style={{ padding: '16px 24px', borderBottom: '1px solid var(--border)',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <ChevronRight size={16} color="#2563EB" />
                <span style={{ fontWeight: 700, fontSize: 15 }}>
                  {sidePanel.mode === 'new' ? 'Nouveau modèle' : (sidePanel.data?.nom || 'Modèle')}
                </span>
              </div>
              <button onClick={() => setSidePanel(null)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF',
                  borderRadius: 6, padding: 4, display: 'flex' }}>
                <X size={18} />
              </button>
            </div>
            {/* Formulaire scrollable */}
            <CampagneTemplateForm
              key={sidePanel.data?.template_id || 'new'}
              initial={sidePanel.data}
              onSave={handleSave}
              onCancel={() => setSidePanel(null)}
              onDuplicate={sidePanel.mode === 'edit' ? () => handleDuplicate(sidePanel.data) : undefined}
              onDelete={sidePanel.mode === 'edit' ? () => handleDelete(sidePanel.data) : undefined}
              logoUrl={logoUrl}
            />
          </div>
        </>
      )}

      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); opacity: 0; }
          to   { transform: translateX(0);    opacity: 1; }
        }
      `}</style>
    </div>
  );
}

// ─── Onglets principaux ────────────────────────────────────────────────────────
const TABS = [
  { id: 'campagne',       label: 'Campagne' },
  { id: 'transactionnel', label: 'Transactionnel' },
];

export default function EmailModeles() {
  const [tab, setTab] = useState('campagne');
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div>
        <h2 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>Modèles d'emails</h2>
      </div>
      <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid var(--border)' }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-modeles-${t.id}`}
            style={{ padding: '8px 20px', fontSize: 13, fontWeight: tab === t.id ? 600 : 400,
              color: tab === t.id ? '#2563EB' : 'var(--text-muted)', background: 'transparent',
              border: 'none', borderBottom: tab === t.id ? '2px solid #2563EB' : '2px solid transparent',
              cursor: 'pointer', marginBottom: -1, transition: 'all 0.15s' }}>
            {t.label}
          </button>
        ))}
      </div>
      <div>
        {tab === 'campagne'       && <OngletCampagne />}
        {tab === 'transactionnel' && <ModeleEmails />}
      </div>
    </div>
  );
}
